import axios from "axios";
import { User } from "@/models/User";
import { Permission } from "@/models/Permission";
import Vue from "vue";
import { computeBackoffDelay } from "./authShared";

export interface UserInfoResponse {
    user: User;
    permissions: Permission[];
    pageNames?: Record<string, string>;
    remoteDisabledStates?: string[];
    stateRestrictedStates?: string[];
}

export enum UserInfoError {
    NETWORK_ERROR = 'NETWORK_ERROR',
    AUTH_ERROR = 'AUTH_ERROR',
    NO_USER_DATA = 'NO_USER_DATA',
    UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

export class UserInfoServiceError extends Error {
    constructor(
        public readonly type: UserInfoError,
        message: string,
        public readonly originalError?: any
    ) {
        super(message);
        this.name = 'UserInfoServiceError';
    }
}

export interface UserInfoServiceOptions {
    forceFresh?: boolean;
}

class UserInfoService {
    private isFetching = false;
    private fetchPromise: Promise<UserInfoResponse> | null = null;
    private lastFetchTime = 0;
    private readonly CACHE_DURATION = 5_000; // 5 seconds cache to prevent rapid refetches

    // Bumped by clearUserData so a fetch still in flight at sign-out is recognized as
    // belonging to the previous session and discarded instead of repopulating the cache.
    private generation = 0;
    
    // Exponential backoff for failures
    private failureCount = 0;
    private lastFailureTime = 0;
    private readonly backoffConfig = {
        baseDelay: 1000,      // Start with 1 second
        maxDelay: 60000,      // Max 1 minute
        jitterPercent: 0.3,   // 30% jitter
        maxFailures: 5        // Max consecutive failures before long backoff
    };

    async fetchUserInfoIfNeeded(excludeUrl?: string): Promise<UserInfoResponse | null> {
        if (!this.shouldFetchUserInfo(excludeUrl)) {
            return Promise.resolve(null);
        }
        
        // Check if we're in a backoff period
        if (this.isInBackoffPeriod()) {
            // Return cached data if available during backoff
            if (this.hasUserData()) {
                return this.getCurrentUserData();
            }
            return Promise.resolve(null);
        }
        
        return this.fetchUserInfo();
    }

    async fetchUserInfo(options: UserInfoServiceOptions = {}): Promise<UserInfoResponse> {
        const { forceFresh = false } = options;

        if (this.isFetching && this.fetchPromise && !forceFresh) {
            return this.fetchPromise;
        }

        if (!forceFresh && this.hasRecentUserData()) {
            return this.getCurrentUserData();
        }

        this.isFetching = true;
        this.fetchPromise = this.performFetch();

        try {
            const result = await this.fetchPromise;
            this.lastFetchTime = Date.now();
            
            // Reset failure count on success
            this.failureCount = 0;
            this.lastFailureTime = 0;
            
            return result;
        } catch (error) {
            // Track failure for exponential backoff
            this.failureCount++;
            this.lastFailureTime = Date.now();
            throw error;
        } finally {
            this.isFetching = false;
            this.fetchPromise = null;
        }
    }

    private async performFetch(): Promise<UserInfoResponse> {
        const generation = this.generation;

        try {
            console.log("Fetching user information...");

            const endpoint = "api/user/info";

            const response = await axios.get<{
                user: User;
                permissions: Permission[];
                pageNames?: Record<string, string>;
                remoteDisabledStates?: string[];
                stateRestrictedStates?: string[];
            }>(endpoint);

            const data = response.data;

            if (!data.user?.userName) {
                throw new UserInfoServiceError(
                    UserInfoError.NO_USER_DATA,
                    "No user information available in response"
                );
            }

            // Create properly typed objects
            const user = new User(data.user);

            const permissions = data.permissions.map(p => new Permission(p));
            const pageNames = data.pageNames || {};
            const remoteDisabledStates = data.remoteDisabledStates ?? [];
            const stateRestrictedStates = data.stateRestrictedStates ?? [];

            // Update Vue prototypes, unless the session ended while this was in flight —
            // the payload describes the signed-out user and would resurrect them.
            if (generation === this.generation) {
                Vue.prototype.$user = user;
                Vue.prototype.$permissions = permissions;
                Vue.prototype.$remoteDisabledStates = remoteDisabledStates;
                Vue.prototype.$stateRestrictedStates = stateRestrictedStates;
                if (data.pageNames) {
                    Vue.prototype.$pageNames = pageNames;
                }
            }

            console.log("User information fetched successfully");

            return {
                user,
                permissions,
                pageNames,
                remoteDisabledStates,
                stateRestrictedStates
            };

        } catch (error) {
            console.error("Error fetching user info:", error);

            const err = error as any;

            // Classify the error type for better handling
            if (error instanceof UserInfoServiceError) {
                throw error;
            }

            if (err?.response?.status === 401) {
                throw new UserInfoServiceError(
                    UserInfoError.AUTH_ERROR,
                    "Authentication required",
                    error
                );
            }

            if (err?.code === 'NETWORK_ERROR' || !navigator.onLine) {
                throw new UserInfoServiceError(
                    UserInfoError.NETWORK_ERROR,
                    "Network connectivity issue",
                    error
                );
            }

            throw new UserInfoServiceError(
                UserInfoError.UNKNOWN_ERROR,
                `Failed to fetch user info: ${err.message || 'Unknown error'}`,
                error
            );
        }
    }

    private hasRecentUserData(): boolean {
        const hasUserData = Vue.prototype.$user?.userName &&
            Vue.prototype.$permissions?.length;
        const isRecent = (Date.now() - this.lastFetchTime) < this.CACHE_DURATION;

        return hasUserData && isRecent;
    }

    private hasUserData(): boolean {
        return !!(Vue.prototype.$user?.userName && Vue.prototype.$permissions?.length);
    }

    private isInBackoffPeriod(): boolean {
        if (this.failureCount === 0) return false;

        const delayWithJitter = computeBackoffDelay(this.failureCount - 1, this.backoffConfig);
        const timeSinceFailure = Date.now() - this.lastFailureTime;

        return timeSinceFailure < delayWithJitter;
    }

    private getCurrentUserData(): UserInfoResponse {
        return {
            user: Vue.prototype.$user,
            permissions: Vue.prototype.$permissions,
            pageNames: Vue.prototype.$pageNames,
            remoteDisabledStates: Vue.prototype.$remoteDisabledStates,
            stateRestrictedStates: Vue.prototype.$stateRestrictedStates
        };
    }

    /**
     * Check if user info should be fetched based on current state
     */
    private shouldFetchUserInfo(excludeUrl?: string): boolean {
        // Don't fetch if we're already fetching
        if (this.isFetching) {
            return false;
        }

        // Don't fetch if we're calling the user info endpoint itself
        if (excludeUrl?.toLowerCase().includes("user/info")) {
            return false;
        }

        // Fetch if we don't have user data
        if (!Vue.prototype.$user?.userName || !Vue.prototype.$permissions?.length) {
            return true;
        }

        return false;
    }

    /**
     * Clear cached user data (useful for logout scenarios)
     */
    clearUserData(): void {
        this.generation++;
        Vue.prototype.$user = new User();
        Vue.prototype.$permissions = [];
        Vue.prototype.$pageNames = {};
        Vue.prototype.$remoteDisabledStates = [];
        Vue.prototype.$stateRestrictedStates = [];
        this.lastFetchTime = 0;
        
        // Reset failure tracking
        this.failureCount = 0;
        this.lastFailureTime = 0;
    }
}

// Export singleton instance
export const userInfoService = new UserInfoService();
export default userInfoService;